import { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  IconButton,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { colors } from '@src/styles';
import {
  blurBackdropSX,
  ghostButtonSX,
  monoFontFamily,
  monoLabelSX,
  primaryButtonSX,
  titleFontFamily,
} from '../../scriptsStyles';
import {
  getPreSignedUploadUrl,
  uploadFileToS3,
  implementCanonicalScript,
  downloadFromBucket,
} from '../../hooks/scripts';
import { useCanonicalScriptsByOrg } from '../../hooks/useCanonicalScriptsByOrg';
import { useError } from '@src/contexts/ErrorContext';
import { buildStepConfigs, ERROR_MAP, STEP_KEYS } from './stepConfigs';
import { convertXlsxToParquet } from '../../utils/convertXlsxToParquet';
import { StepKey } from './types';
import { fadeInSX } from '@src/styles/animations';

interface CreateCanonicalScriptModalProps {
  open: boolean;
  onClose: () => void;
  organizations: Record<string, string>;
  isLoadingOrganizations: boolean;
}

export const CreateCanonicalScriptModal = ({
  open,
  onClose,
  organizations,
  isLoadingOrganizations,
}: CreateCanonicalScriptModalProps) => {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [selectedOrganization, setSelectedOrganization] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [formData, setFormData] = useState<any>({});
  const [isLoading, setIsLoading] = useState(false);
  const { setError, setSuccess } = useError();

  const { data: templates = [], isLoading: isLoadingTemplates } = useCanonicalScriptsByOrg(selectedOrganization);

  const currentTemplate = useMemo(
    () => templates.find((t) => t.id === selectedTemplate),
    [templates, selectedTemplate],
  );

  const stepConfigs = useMemo(() => buildStepConfigs(currentTemplate), [currentTemplate]);

  // Reset activeStep quando os steps mudarem
  useEffect(() => {
    if (activeStep >= stepConfigs.length && stepConfigs.length > 0) {
      setActiveStep(stepConfigs.length - 1);
    }
  }, [stepConfigs.length, activeStep]);

  const handleDownloadTemplate = async () => {
    if (!currentTemplate) return;

    try {
      await downloadFromBucket(currentTemplate.modelDocumentPath, `Template-${currentTemplate.key}-Download.xlsx`);
      setSuccess({
        title: 'Download iniciado',
        message: 'O template está sendo baixado',
      });
    } catch (error: any) {
      console.error('Error downloading template:', error);
      setError({
        title: 'Erro ao baixar template',
        message: error?.message || 'Não foi possível baixar o template',
        statusCode: error?.response?.status,
      });
    }
  };

  const handleTemplateChange = (templateId: string) => {
    setSelectedTemplate(templateId);
    // Reset campos que dependem do template
    setSelectedFile(null);
    setFormData({});
  };

  const handleOrganizationChange = (organizationId: string) => {
    setSelectedOrganization(organizationId);
    setSelectedTemplate('');
    setSelectedFile(null);
    setFormData({});
  };

  const canProceedCurrentStep = () => {
    const currentStepKey = stepConfigs[activeStep]?.stepKey;

    switch (currentStepKey) {
      case STEP_KEYS.SELECT_ORGANIZATION:
        return selectedOrganization !== '';
      case STEP_KEYS.LIST_ORG_CANONICALS:
        return selectedTemplate !== '';
      case STEP_KEYS.UPLOAD_SPREADSHEET:
        return selectedFile !== null;
      case STEP_KEYS.CONFIGURE_SCRIPT:
        return formData && Object.keys(formData).length > 0;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (!canProceedCurrentStep()) {
      const currentStepKey = stepConfigs[activeStep]?.stepKey;
      setError({
        title: 'Campo obrigatório',
        message: ERROR_MAP[currentStepKey as StepKey] ?? 'Por favor, preencha todos os campos',
      });
      return;
    }

    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    if (!selectedOrganization || !selectedTemplate || !currentTemplate) {
      setError({
        title: 'Campos obrigatórios',
        message: 'Por favor, preencha todos os campos',
      });
      return;
    }

    // Validar se tem upload quando necessário
    if (currentTemplate.modelDocumentPath && !selectedFile) {
      setError({
        title: 'Upload obrigatório',
        message: 'Por favor, faça upload da planilha',
      });
      return;
    }

    // Validar se tem configuração quando necessário
    if (currentTemplate.schema && (!formData || Object.keys(formData).length === 0)) {
      setError({
        title: 'Configuração obrigatória',
        message: 'Por favor, configure o script',
      });
      return;
    }

    setIsLoading(true);
    try {
      let uploadKey: string | undefined;

      // Se precisa fazer upload
      if (currentTemplate.modelDocumentPath && selectedFile) {
        const fileToUpload = await convertXlsxToParquet(selectedFile);
        const { uploadUrl, key } = await getPreSignedUploadUrl(
          fileToUpload.name,
          fileToUpload.type,
          selectedOrganization,
        );
        await uploadFileToS3(uploadUrl, fileToUpload);
        uploadKey = key;
      }

      await implementCanonicalScript(
        currentTemplate.key,
        selectedOrganization,
        uploadKey,
        currentTemplate.schema ? formData : undefined,
      );

      setSuccess({
        title: 'Script em processamento',
        message: 'Seu script canônico está sendo criado',
      });

      handleClose();
    } catch (error: any) {
      console.error('Error creating canonical script:', error);
      setError({
        title: 'Erro ao criar script',
        message: error?.response?.data?.message || error?.message || 'Não foi possível criar o script canônico',
        statusCode: error?.response?.status,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setActiveStep(0);
    setSelectedTemplate('');
    setSelectedOrganization('');
    setSelectedFile(null);
    setFormData({});
    onClose();
  };

  const renderCurrentStep = () => {
    const currentStepConfig = stepConfigs[activeStep];
    if (!currentStepConfig) return null;

    const StepComponent = currentStepConfig.component;
    const currentStepKey = currentStepConfig.stepKey;

    const baseProps = {
      onNext: handleNext,
      onBack: handleBack,
      canProceed: canProceedCurrentStep(),
      isLoading,
    };

    switch (currentStepKey) {
      case STEP_KEYS.SELECT_ORGANIZATION:
        return (
          <StepComponent
            {...baseProps}
            organizations={organizations}
            selectedOrganization={selectedOrganization}
            onOrganizationChange={handleOrganizationChange}
            isLoadingOrganizations={isLoadingOrganizations}
          />
        );
      case STEP_KEYS.LIST_ORG_CANONICALS:
        return (
          <StepComponent
            {...baseProps}
            templates={templates}
            selectedTemplate={selectedTemplate}
            onTemplateChange={handleTemplateChange}
            onDownloadTemplate={handleDownloadTemplate}
            isLoadingTemplates={isLoadingTemplates}
          />
        );
      case STEP_KEYS.UPLOAD_SPREADSHEET:
        return <StepComponent {...baseProps} selectedFile={selectedFile} onFileSelect={setSelectedFile} />;
      case STEP_KEYS.CONFIGURE_SCRIPT:
        return (
          <StepComponent
            {...baseProps}
            schema={currentTemplate?.schema}
            formData={formData}
            onFormDataChange={setFormData}
          />
        );
      default:
        return null;
    }
  };

  const isLastStep = activeStep === stepConfigs.length - 1;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      slotProps={{ backdrop: { sx: blurBackdropSX } }}
      PaperProps={{
        sx: {
          bgcolor: colors.brand.surface,
          backgroundImage: 'none',
          color: colors.primary.white,
          border: `1px solid ${colors.brand.line}`,
          borderRadius: '16px',
          boxShadow: '0 30px 80px rgba(0, 0, 0, 0.5)',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, padding: '20px 24px 16px' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={monoLabelSX}>
            Etapa {Math.min(activeStep + 1, stepConfigs.length)} de {stepConfigs.length}
          </Typography>
          <Typography
            component="h2"
            sx={{ fontFamily: titleFontFamily, fontSize: '20px', fontWeight: 500, letterSpacing: '-0.02em', mt: 0.5 }}
          >
            Novo script canônico
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          aria-label="Fechar"
          sx={{
            width: 32,
            height: 32,
            color: colors.brand.textSecondary,
            '& svg': { color: 'inherit' },
            '&:hover': { color: colors.primary.white, backgroundColor: 'rgba(255, 160, 21, 0.1)' },
          }}
        >
          <CloseRoundedIcon sx={{ fontSize: 19 }} />
        </IconButton>
      </Box>

      <Box
        component="ol"
        aria-label="Etapas"
        sx={{
          listStyle: 'none',
          margin: 0,
          padding: '0 24px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
          borderBottom: `1px solid ${colors.brand.line}`,
        }}
      >
        {stepConfigs.map((config, index) => {
          const isDone = index < activeStep;
          const isActive = index === activeStep;
          const tone = isDone ? colors.status.concluded : isActive ? colors.primary.yellow : colors.brand.textMuted;
          return (
            <Box
              component="li"
              key={config.stepKey}
              aria-current={isActive ? 'step' : undefined}
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              {index > 0 && (
                <Box
                  aria-hidden
                  sx={{
                    width: 20,
                    height: '1px',
                    backgroundColor: isDone || isActive ? `${colors.primary.yellow}66` : colors.brand.line,
                  }}
                />
              )}
              <Box
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontFamily: monoFontFamily,
                  fontSize: '11px',
                  color: isActive ? colors.brand.ink : tone,
                  backgroundColor: isActive ? colors.primary.yellow : `${tone}1A`,
                  border: `1px solid ${isActive ? colors.primary.yellow : `${tone}55`}`,
                  transition: 'all 0.2s ease',
                }}
              >
                {isDone ? <CheckRoundedIcon sx={{ fontSize: 14, color: `${tone} !important` }} /> : index + 1}
              </Box>
              <Typography
                sx={{
                  fontSize: '12.5px',
                  fontWeight: isActive ? 600 : 400,
                  color: isActive ? colors.primary.white : isDone ? colors.brand.textStrong : colors.brand.textMuted,
                  whiteSpace: 'nowrap',
                }}
              >
                {config.label}
              </Typography>
            </Box>
          );
        })}
      </Box>

      <DialogContent sx={{ padding: '20px 24px !important' }}>
        <Box key={stepConfigs[activeStep]?.stepKey} sx={{ minHeight: 170, ...fadeInSX() }}>
          {renderCurrentStep()}
        </Box>
      </DialogContent>

      <DialogActions sx={{ borderTop: `1px solid ${colors.brand.line}`, padding: '14px 24px', gap: 1 }}>
        <Button onClick={handleClose} disabled={isLoading} sx={{ ...ghostButtonSX, border: 'none', mr: 'auto' }}>
          Cancelar
        </Button>
        {activeStep > 0 && (
          <Button onClick={handleBack} disabled={isLoading} sx={ghostButtonSX}>
            Voltar
          </Button>
        )}
        {!isLastStep ? (
          <Button
            onClick={handleNext}
            variant="contained"
            disableElevation
            disabled={!canProceedCurrentStep() || isLoading}
            sx={primaryButtonSX}
          >
            Próximo
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            variant="contained"
            disableElevation
            disabled={!canProceedCurrentStep() || isLoading}
            startIcon={isLoading && <CircularProgress size={16} sx={{ color: 'inherit' }} />}
            sx={primaryButtonSX}
          >
            {isLoading ? 'Criando…' : 'Criar script'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
